/* BobaFashion cart drawer enhancements
   - Volume-discount progress bar (reads tier config from #CartDrawer data-attrs)
   - Save badge injection on each line item
   - Empty-state class toggle
   - "Others also bought" carousel + add-to-cart without closing drawer
   Listens to:
     - 'cart:updated'  (dispatched by theme.js after every quantity change)
     - 'cart:build'    (dev-friendly rebuild trigger)
     - 'cart:added'    (dispatched here after upsell add)
   Reads cart state from /cart.js when needed. */

(function () {
  'use strict';

  function ready(fn) {
    if (document.readyState !== 'loading') { fn(); }
    else { document.addEventListener('DOMContentLoaded', fn); }
  }

  function fetchCart() {
    return fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); });
  }

  function formatMoney(cents, format) {
    if (window.theme && theme.Currency && theme.Currency.formatMoney) {
      return theme.Currency.formatMoney(cents, format);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  function t(key, fallback, vars) {
    var s = (window.theme && theme.strings && theme.strings[key]) || fallback;
    if (vars) {
      Object.keys(vars).forEach(function (k) {
        s = s.replace('{{ ' + k + ' }}', vars[k]).replace('{{' + k + '}}', vars[k]);
      });
    }
    return s;
  }

  /* ---------- Progress bar ---------- */

  function readTiers(drawer) {
    var tierCount = parseInt(drawer.dataset.tierCount || '4', 10);
    var tiers = [];
    for (var i = 1; i <= tierCount; i++) {
      var qty = parseInt(drawer.dataset['tier' + i + 'Qty'] || drawer.getAttribute('data-tier-' + i + '-qty') || '0', 10);
      var pct = parseInt(drawer.dataset['tier' + i + 'Pct'] || drawer.getAttribute('data-tier-' + i + '-pct') || '0', 10);
      if (qty > 0) tiers.push({ qty: qty, pct: pct });
    }
    return tiers.sort(function (a, b) { return a.qty - b.qty; });
  }

  function updateProgress(drawer, itemCount) {
    var wrap = drawer.querySelector('[data-progress]');
    if (!wrap) return;

    var tiers = readTiers(drawer);
    if (!tiers.length) { wrap.style.display = 'none'; return; }

    var msg = wrap.querySelector('[data-progress-msg]');
    var fill = wrap.querySelector('[data-progress-fill]');
    var steps = wrap.querySelectorAll('[data-progress-step]');

    /* Mark reached steps */
    var reachedTiers = tiers.filter(function (tier) { return itemCount >= tier.qty; });
    var currentTier = reachedTiers.length ? reachedTiers[reachedTiers.length - 1] : null;
    var nextTier = tiers[reachedTiers.length] || null;

    steps.forEach(function (step) {
      var stepQty = parseInt(step.dataset.progressStepQty, 10);
      step.classList.toggle('is-reached', itemCount >= stepQty);
    });

    /* Fill width: 0% before first tier, then proportional toward next, 100% if all reached */
    var pctFilled;
    if (!reachedTiers.length) {
      pctFilled = Math.min((itemCount / tiers[0].qty) * (100 / tiers.length), 100 / tiers.length);
    } else if (!nextTier) {
      pctFilled = 100;
    } else {
      var idx = reachedTiers.length;
      var basePct = (idx / tiers.length) * 100;
      var stepPct = (1 / tiers.length) * 100;
      var progressInTier = (itemCount - currentTier.qty) / (nextTier.qty - currentTier.qty);
      pctFilled = basePct + Math.max(0, Math.min(1, progressInTier)) * stepPct;
    }
    if (fill) fill.style.width = pctFilled + '%';

    /* Status message */
    if (msg) {
      if (!nextTier) {
        msg.textContent = t(
          'cartDrawerMaxUnlocked',
          "You've unlocked the maximum {{ pct }}% extra discount!",
          { pct: currentTier.pct }
        );
      } else {
        var diff = nextTier.qty - itemCount;
        var template = diff === 1
          ? t('cartDrawerAddOneMore', 'Add 1 more item to save an extra {{ pct }}%!', { pct: nextTier.pct })
          : t('cartDrawerAddMore', 'Add {{ count }} more items to save an extra {{ pct }}%!', { count: diff, pct: nextTier.pct });
        msg.textContent = template;
      }
    }
  }

  /* ---------- Save badges + line item polish ---------- */

  function injectSaveBadges(drawer) {
    drawer.querySelectorAll('.cart__item-price-col').forEach(function (col) {
      var was = col.querySelector('.cart__price--strikethrough');
      var now = col.querySelector('.cart__price.cart__discount, .cart__price:not(.cart__price--strikethrough)');
      var existing = col.querySelector('.cart-drawer-fm__save');
      if (existing) existing.remove();
      if (!was || !now) return;
      var wasCents = parseMoney(was.textContent);
      var nowCents = parseMoney(now.textContent);
      if (wasCents > nowCents && wasCents > 0) {
        var saveCents = wasCents - nowCents;
        var format = drawer.dataset.moneyFormat || '${{amount}}';
        var span = document.createElement('span');
        span.className = 'cart-drawer-fm__save';
        span.textContent = '(' + t('cartDrawerSave', 'Save {{ amount }}', { amount: formatMoney(saveCents, format) }) + ')';
        col.appendChild(span);
      }
    });
  }

  function parseMoney(str) {
    if (!str) return 0;
    var clean = str.replace(/[^\d.,-]/g, '').replace(/\.(?=\d{3}(\D|$))/g, '');
    var lastComma = clean.lastIndexOf(',');
    var lastDot = clean.lastIndexOf('.');
    var decSep = lastComma > lastDot ? ',' : '.';
    if (decSep === ',') clean = clean.replace(/\./g, '').replace(',', '.');
    else clean = clean.replace(/,/g, '');
    var num = parseFloat(clean);
    return isNaN(num) ? 0 : Math.round(num * 100);
  }

  /* ---------- Empty state ---------- */

  function applyEmptyState(drawer, itemCount) {
    drawer.classList.toggle('is-empty', itemCount <= 0);
  }

  /* ---------- Sync from cart object ---------- */

  function syncFromCart(drawer, cart) {
    if (!cart) return;
    updateProgress(drawer, cart.item_count || 0);
    applyEmptyState(drawer, cart.item_count || 0);
    injectSaveBadges(drawer);
  }

  /* ---------- Recommendations carousel ---------- */

  function initRecs(drawer) {
    var recs = drawer.querySelector('[data-recs]');
    if (!recs) return;
    var track = recs.querySelector('[data-recs-track]');
    var prev = recs.querySelector('[data-recs-prev]');
    var next = recs.querySelector('[data-recs-next]');
    if (!track) return;

    function step() { return track.firstElementChild ? track.firstElementChild.getBoundingClientRect().width + 12 : 200; }
    function updateArrows() {
      if (prev) prev.disabled = track.scrollLeft <= 4;
      if (next) next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
    }

    if (prev) prev.addEventListener('click', function () { track.scrollBy({ left: -step(), behavior: 'smooth' }); });
    if (next) next.addEventListener('click', function () { track.scrollBy({ left: step(), behavior: 'smooth' }); });
    track.addEventListener('scroll', updateArrows, { passive: true });
    setTimeout(updateArrows, 50);

    recs.querySelectorAll('[data-rec-add]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var card = btn.closest('[data-rec-product]');
        var variantEl = card && card.querySelector('[data-rec-variant]');
        var variantId = variantEl && (variantEl.value || variantEl.getAttribute('value'));
        if (!variantId) return;
        btn.disabled = true;
        var originalText = btn.textContent;
        fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1 })
        }).then(function (r) { return r.json(); }).then(function () {
          btn.classList.add('is-added');
          btn.textContent = t('cartDrawerAdded', 'Added');
          document.dispatchEvent(new CustomEvent('cart:build'));
          fetchCart().then(function (cart) {
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
          });
          setTimeout(function () { btn.classList.remove('is-added'); btn.textContent = originalText; btn.disabled = false; }, 1600);
        }).catch(function () {
          btn.disabled = false;
        });
      });
    });
  }

  /* ---------- Boot ---------- */

  ready(function () {
    var drawer = document.getElementById('CartDrawer');
    if (!drawer || !drawer.classList.contains('cart-drawer-fm')) return;

    /* Initial sync */
    fetchCart().then(function (cart) { syncFromCart(drawer, cart); });

    /* Re-sync after every cart update */
    document.addEventListener('cart:updated', function (evt) {
      var cart = evt.detail && evt.detail.cart;
      if (cart) {
        syncFromCart(drawer, cart);
      } else {
        fetchCart().then(function (c) { syncFromCart(drawer, c); });
      }
    });

    /* If theme.js dispatches cart:build, items will be re-rendered — re-decorate after a tick */
    document.addEventListener('cart:build', function () {
      setTimeout(function () {
        fetchCart().then(function (c) { syncFromCart(drawer, c); });
      }, 250);
    });

    initRecs(drawer);
  });
})();
